<script setup lang="ts">
defineProps({
  position: {
    type: String,
    default: 'top-right',
  },
});

const { toaster, isModalOpen } = storeToRefs(useLayoutStore());

const { currentRouteName } = useRoutes();

const progressBarTimer = ref(100);

function closeToaster() {
  if (!toaster.value) {
    return;
  }

  toaster.value.is_open = false;

  const { isMobile } = useLayout();
  const defaultPosition = isMobile.value ? 'bottom-right' : 'top-right';

  setTimeout(() => {
    toaster.value.position = defaultPosition;
    toaster.value.type = 'default';
    toaster.value.message = '';
    toaster.value.time = 5000;
  }, 220);
}

watch(
  () => toaster.value.is_open,
  (value: boolean) => {
    if (value) {
      const interval = setInterval(() => {
        if (!toaster.value || !progressBarTimer.value) {
          return;
        }

        if (progressBarTimer.value > 0 && !!toaster.value.time) {
          progressBarTimer.value -= 100 / (toaster.value.time / 100);
        } else {
          clearInterval(interval);
          closeToaster();
        }
      }, 100);

      watch(
        () => toaster.value.is_open,
        (value: boolean) => {
          if (!value) {
            if (!toaster.value || !progressBarTimer.value) {
              return;
            }

            clearInterval(interval);
            closeToaster();
            progressBarTimer.value = 100;
          }
        },
      );
    }
  },
);
</script>

<template>
  <Teleport to="body">
    <div
      class="transition"
      :class="[
        $style.toaster,
        $style[toaster.type],
        $style[toaster.position],
        $style[currentRouteName ?? ''],
        { [$style.isOpen]: toaster.is_open },
        { [$style.modalOpen]: isModalOpen },
      ]"
      @click="closeToaster"
    >
      <div :class="$style.content">
        <NuxtIcon
          v-if="toaster.type !== 'default'"
          aria-hidden="true"
          :class="[$style.typeIcon, $style[toaster.type]]"
          :name="`toaster/${toaster.type}`"
        />
        <span v-html="toaster.message"></span>
      </div>
      <div
        :class="$style.closeIcon"
        @click="closeToaster"
      >
        <NuxtIcon name="close" />
      </div>

      <div :class="$style.progressBar">
        <div
          :class="[$style.progressBarTimer, $style[toaster.type]]"
          :style="{ width: `${progressBarTimer}%` }"
        />
      </div>
    </div>
  </Teleport>
</template>

<style module lang="scss">
.toaster {
  @apply fixed m-4 z-50 bg-black/95 text-white rounded-lg cursor-pointer flex justify-between items-center gap-3 px-3.5 pt-3 pb-4 opacity-0 invisible select-none transition-all duration-200 overflow-hidden;

  &.isOpen {
    @apply opacity-100 visible;
    z-index: 9999999999;
  }

  &.top-right {
    @apply top-0 right-0 -translate-y-16;

    &.isOpen {
      @apply translate-y-0;
    }
  }

  &.top-left {
    @apply top-0 left-0 -translate-y-16;

    &.isOpen {
      @apply translate-y-0;
    }
  }

  &.top-center {
    @apply top-0 left-1/2 -translate-x-1/2 -translate-y-16;

    &.isOpen {
      @apply translate-y-0 -translate-x-1/2;
    }
  }

  &.top-screen {
    @apply top-0 left-0 right-0 rounded-none m-0 -translate-y-16;

    &.isOpen {
      @apply translate-y-0;
    }
  }

  &.bottom-right {
    @apply bottom-0 right-0 translate-y-16;

    &.default:not(.modalOpen):not(.pending) {
      @apply bottom-[57.52px] md:bottom-0;
    }

    &.isOpen {
      @apply translate-y-0;
    }
  }

  &.bottom-left {
    @apply bottom-0 left-0 translate-y-16;

    &.default:not(.modalOpen):not(.pending) {
      @apply bottom-[57.52px] md:bottom-0;
    }

    &.isOpen {
      @apply translate-y-0;
    }
  }

  &.bottom-center {
    @apply bottom-0 left-1/2 -translate-x-1/2 translate-y-16;

    &.default:not(.modalOpen):not(.pending) {
      @apply bottom-[57.52px] md:bottom-0;
    }

    &.isOpen {
      @apply translate-y-0;
    }
  }

  &.bottom-screen {
    @apply bottom-0 left-0 right-0 rounded-none m-0 translate-y-16;

    &.default:not(.modalOpen):not(.pending) {
      @apply bottom-[57.52px] md:bottom-0;
    }

    &.isOpen {
      @apply translate-y-0;
    }
  }

  .content {
    @apply text-xs font-semibold flex items-center gap-3 max-w-full md:max-w-96;

    small {
      @apply opacity-50 text-[.68rem] leading-3 inline-flex max-w-[90vw] overflow-hidden;
    }

    .typeIcon {
      @apply text-base;

      &.success {
        @apply text-success;
      }
      &.error {
        @apply text-error;
      }
      &.info {
        @apply text-info;
      }
      &.warning {
        @apply text-warning;
      }
    }
  }

  .closeIcon {
    @apply cursor-pointer opacity-50 text-base hover:opacity-90;
  }

  .progressBar {
    @apply absolute bottom-0 left-0 right-0 h-1 bg-white/20;

    .progressBarTimer {
      @apply h-full;
      transition: width 0.2s ease-in-out !important;

      &.default {
        @apply bg-primary;
      }
      &.success {
        @apply bg-success;
      }
      &.error {
        @apply bg-error;
      }
      &.info {
        @apply bg-info;
      }
      &.warning {
        @apply bg-warning;
      }
    }
  }
}
</style>
